import React, { useEffect, useState } from 'react';
import { Input, Form, Button, Select, Upload, Space, Image, Spin, notification } from 'antd';
import { handleFormChange, layout, validateMessages } from '../../../shared/Utils';
import { Operator } from '../../../shared/types';
import { useUpdateOperatorMutation } from '../../../GraphQL/hooks/useUpdateOperatorMutation';
import { PlusOutlined } from '@ant-design/icons';
import ImageUploadPreview from '../../../shared/Components/ImageUploadPreview';

const BusinessData = ({ businessData }) => {
    const [form] = Form.useForm();
    const [formError, setFormError] = useState(false);

    const [newOperator, setNewOperator] = useState<Operator>();
    const { mutation, loading: creating } = useUpdateOperatorMutation();

    const [fileUpload, setFileUpload] = useState<any>();

    useEffect(() => {
        setNewOperator(businessData);
        setFileUpload(businessData?.logo);
        form.setFieldsValue(businessData);
    }, []);

    useEffect(() => {
        form.setFieldsValue(newOperator);
    }, [form, creating]);

    const onFinish = async () => {
        const values = await form.validateFields();
        const { logo, ...valuesWithoutLogo } = values;

        try {
            if (values) {
                await mutation({ ...valuesWithoutLogo, _id: businessData._id }, fileUpload)
                    .then((newOperator) => {
                        setNewOperator(newOperator);
                    })
                    .finally(() => {
                        notification.success({
                            message: 'Business data updated successfully',
                            description: 'The business data has been updated successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to update business data',
                            description: 'An error occurred while updating the business data.',
                        });

                        console.error('Failed to update business data:', err);
                    });
            } else {
                notification.error({
                    message: 'Form validation failed',
                    description: 'Please check the form for errors and try again.',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to update business data',
                description: 'An error occurred while updating the business data.',
            });
            console.error('Failed to update business data:', error);
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Spin spinning={creating}>
            <Form<Operator>
                {...layout}
                form={form}
                name="BusinessData"
                autoComplete="off"
                onFieldsChange={() => {
                    setFormError(handleFormChange(form));
                }}
                onFinish={onFinish}
                validateMessages={validateMessages}
            >
                <Form.Item
                    name={'logo'}
                    label="Logo"
                    valuePropName={'logo'}
                    getValueFromEvent={(event) => event?.file}
                    rules={[
                        {
                            required: false,
                            message: 'please upload your Logo.',
                        },
                        {
                            validator(_, fileList) {
                                return new Promise((resolve, reject) => {
                                    if (fileList?.size > 5000000) {
                                        reject('File size exceeded');
                                    } else {
                                        resolve('Successs');
                                    }
                                });
                            },
                        },
                    ]}
                >
                    <Upload
                        accept="image/png, image/jpeg"
                        maxCount={1}
                        beforeUpload={() => false}
                        onChange={({ file }) => setFileUpload(file)}
                        showUploadList={false}
                        listType="picture-card"
                        openFileDialogOnClick={!fileUpload}
                    >
                        {fileUpload?.size > 0 ? (
                            <ImageUploadPreview file={fileUpload} onDelete={() => setFileUpload(null)} />
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Upload Image</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>
                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                    <Input type="text" disabled={true} style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                    <Input type="phone" placeholder={'72 234 567'} style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item name="email" label="Email" rules={[{ required: true }]}>
                    <Input type="email" style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item name="description" label="Description" rules={[{ required: false }]}>
                    <Input.TextArea showCount maxLength={100} style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item name="tva" label="TVA" rules={[{ required: false }]}>
                    <Input type="text" disabled={true} style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item name="website" label="Website" rules={[{ required: false }]}>
                    <Input type="text" style={{ width: '60%' }} />
                </Form.Item>
                <Form.Item label="Address">
                    <Space.Compact style={{ width: '60%' }}>
                        <Form.Item
                            name={['address', 'street']}
                            noStyle
                            style={{ width: '70%' }}
                            rules={[{ required: false }]}
                        >
                            <Input type="text" placeholder="Street name" />
                        </Form.Item>
                        <Form.Item
                            name={['address', 'postalCode']}
                            noStyle
                            style={{ width: '30%' }}
                            rules={[{ required: false }]}
                        >
                            <Input type="number" placeholder="Postal code" />
                        </Form.Item>
                    </Space.Compact>
                </Form.Item>
                <Form.Item label="Country / City">
                    <Space.Compact style={{ width: '60%' }}>
                        <Form.Item
                            name={['address', 'city']}
                            noStyle
                            style={{ width: '33.333%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select city" disabled={true}>
                                <Select.Option value="Ras Jebel">Ras Jebel</Select.Option>
                                <Select.Option value="Arianna">Arianna</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name={['address', 'governorate']}
                            noStyle
                            style={{ width: '33.333%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select governorate" disabled={true}>
                                <Select.Option value="Bizerte">Bizerte</Select.Option>
                                <Select.Option value="Tunis">Tunis</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name={['address', 'country']}
                            noStyle
                            style={{ width: '33.333%' }}
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select country" disabled={true}>
                                <Select.Option value="Tunisia">Tunisia</Select.Option>
                                <Select.Option value="Algeria">Algeria</Select.Option>
                                <Select.Option value="Marokko">Marokko</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space.Compact>
                </Form.Item>

                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Button type="primary" style={{ marginRight: '8px' }} htmlType="submit" disabled={formError}>
                        Save
                    </Button>
                    <Button htmlType="button" onClick={onReset}>
                        Cancel
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
};

export default BusinessData;
